/**
 * Business Rules Engine
 * 
 * Provides a unified interface for accessing and evaluating tenant-specific
 * business rules across the platform.
 */

import { supabase } from '../integrations/supabase/client';

export interface ReminderRules {
  initial_delay_hours: number;
  followup_delay_hours: number;
  max_reminders: number;
  escalation_after_reminders: number;
  role_specific: Record<string, {
    initial_delay_hours?: number;
    followup_delay_hours?: number;
    max_reminders?: number;
  }>;
}

export interface SlaRules {
  onboarding_target_days: number;
  credit_decision_hours: number;
  document_review_hours: number;
  escalation_thresholds: {
    warning: number;  // 0.75 = 75% of target time
    critical: number; // 1.0 = 100% of target time
  };
}

export interface ApprovalRules {
  credit_auto_approve_limit: number;
  credit_require_director_above: number;
  document_approval_required: boolean;
  multi_level_approval: boolean;
  approval_chain: Array<{
    level: number;
    approver_role: string;
    min_amount?: number;
    max_amount?: number;
  }>;
}

export interface FlowRules {
  require_all_roles: boolean;
  allow_role_skip: string[];
  dependency_chain: Record<string, string[]>;
  auto_complete_on_all_roles: boolean;
}

export interface DocumentRules {
  required_by_client_type: Record<string, string[]>;
  expiry_warning_days: number;
  auto_renewal_reminders: boolean;
}

export interface TenantBusinessRules {
  id: string;
  platformTenantId: string;
  reminderRules: ReminderRules;
  slaRules: SlaRules;
  approvalRules: ApprovalRules;
  flowRules: FlowRules;
  documentRules: DocumentRules;
  onboardingRules: Record<string, unknown>;
  activityRules: Record<string, unknown>;
  notificationRules: Record<string, unknown>;
}

class BusinessRulesEngineClass {
  private rulesCache: Map<string, { rules: TenantBusinessRules; cachedAt: Date }> = new Map();
  private cacheTtlMs = 5 * 60 * 1000; // 5 minutes

  /**
   * Get all business rules for a tenant
   */
  async getTenantRules(platformTenantId: string): Promise<TenantBusinessRules> {
    // Check cache
    const cached = this.rulesCache.get(platformTenantId);
    if (cached && Date.now() - cached.cachedAt.getTime() < this.cacheTtlMs) {
      return cached.rules;
    }

    const { data, error } = await supabase
      .from('tenant_business_rules')
      .select('*')
      .eq('platform_tenant_id', platformTenantId)
      .single();

    if (error) {
      console.error('[BusinessRulesEngine] Error fetching rules:', error);
      // Return defaults if no rules found
      return this.getDefaultRules(platformTenantId);
    }

    const rules = this.mapRules(data);
    this.rulesCache.set(platformTenantId, { rules, cachedAt: new Date() });
    return rules;
  }

  /**
   * Get reminder settings for a specific role
   */
  async getReminderSettings(
    platformTenantId: string,
    roleCode?: string
  ): Promise<{
    initialDelayHours: number;
    followupDelayHours: number;
    maxReminders: number;
    escalationAfterReminders: number;
  }> {
    const rules = await this.getTenantRules(platformTenantId);
    const baseRules = rules.reminderRules;
    const roleRules = roleCode ? baseRules.role_specific[roleCode] : undefined;

    return {
      initialDelayHours: roleRules?.initial_delay_hours ?? baseRules.initial_delay_hours,
      followupDelayHours: roleRules?.followup_delay_hours ?? baseRules.followup_delay_hours,
      maxReminders: roleRules?.max_reminders ?? baseRules.max_reminders,
      escalationAfterReminders: baseRules.escalation_after_reminders,
    };
  }

  /**
   * Get SLA target for a specific type
   */
  async getSlaTarget(
    platformTenantId: string,
    slaType: 'onboarding' | 'credit_decision' | 'document_review'
  ): Promise<{
    targetHours: number;
    warningThreshold: number;
    criticalThreshold: number;
  }> {
    const rules = await this.getTenantRules(platformTenantId);
    const slaRules = rules.slaRules;

    let targetHours: number;
    switch (slaType) {
      case 'onboarding':
        targetHours = slaRules.onboarding_target_days * 24;
        break;
      case 'credit_decision':
        targetHours = slaRules.credit_decision_hours;
        break;
      case 'document_review':
        targetHours = slaRules.document_review_hours;
        break;
    }

    return {
      targetHours,
      warningThreshold: slaRules.escalation_thresholds.warning,
      criticalThreshold: slaRules.escalation_thresholds.critical,
    };
  }

  /**
   * Check if a credit application should be auto-approved
   */
  async shouldAutoApproveCredit(
    platformTenantId: string,
    requestedLimit: number
  ): Promise<{
    autoApprove: boolean;
    requiresDirectorApproval: boolean;
    reason?: string;
  }> {
    const rules = await this.getTenantRules(platformTenantId);
    const approvalRules = rules.approvalRules;

    if (requestedLimit <= approvalRules.credit_auto_approve_limit) {
      return { autoApprove: true, requiresDirectorApproval: false };
    }

    if (requestedLimit > approvalRules.credit_require_director_above) {
      return {
        autoApprove: false,
        requiresDirectorApproval: true,
        reason: `Amount exceeds €${approvalRules.credit_require_director_above.toLocaleString()} - requires director approval`,
      };
    }

    return {
      autoApprove: false,
      requiresDirectorApproval: false,
      reason: `Amount exceeds auto-approve limit of €${approvalRules.credit_auto_approve_limit.toLocaleString()}`,
    };
  }

  /**
   * Get required documents for a client type
   */
  async getRequiredDocuments(
    platformTenantId: string,
    clientType: string
  ): Promise<string[]> {
    const rules = await this.getTenantRules(platformTenantId);
    return rules.documentRules.required_by_client_type[clientType] || [];
  }

  /**
   * Check if a role can be skipped
   */
  async canSkipRole(platformTenantId: string, roleCode: string): Promise<boolean> {
    const rules = await this.getTenantRules(platformTenantId);
    return rules.flowRules.allow_role_skip.includes(roleCode);
  }

  /**
   * Get role dependencies
   */
  async getRoleDependencies(platformTenantId: string, roleCode: string): Promise<string[]> {
    const rules = await this.getTenantRules(platformTenantId);
    return rules.flowRules.dependency_chain[roleCode] || [];
  }

  /**
   * Update tenant rules
   */
  async updateRules(
    platformTenantId: string,
    updates: Partial<{
      reminderRules: Partial<ReminderRules>;
      slaRules: Partial<SlaRules>;
      approvalRules: Partial<ApprovalRules>;
      flowRules: Partial<FlowRules>;
      documentRules: Partial<DocumentRules>;
    }>,
    updatedBy?: string
  ): Promise<void> {
    const currentRules = await this.getTenantRules(platformTenantId);

    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };

    if (updatedBy) {
      updateData.updated_by = updatedBy;
    }

    if (updates.reminderRules) {
      updateData.reminder_rules = { ...currentRules.reminderRules, ...updates.reminderRules };
    }
    if (updates.slaRules) {
      updateData.sla_rules = { ...currentRules.slaRules, ...updates.slaRules };
    }
    if (updates.approvalRules) {
      updateData.approval_rules = { ...currentRules.approvalRules, ...updates.approvalRules };
    }
    if (updates.flowRules) {
      updateData.flow_rules = { ...currentRules.flowRules, ...updates.flowRules };
    }
    if (updates.documentRules) {
      updateData.document_rules = { ...currentRules.documentRules, ...updates.documentRules };
    }

    const { error } = await supabase
      .from('tenant_business_rules')
      .update(updateData)
      .eq('platform_tenant_id', platformTenantId);

    if (error) {
      throw new Error(`Failed to update rules: ${error.message}`);
    }

    // Invalidate cache
    this.rulesCache.delete(platformTenantId);
  }

  /**
   * Clear cache for a tenant
   */
  clearCache(platformTenantId?: string): void {
    if (platformTenantId) {
      this.rulesCache.delete(platformTenantId);
    } else {
      this.rulesCache.clear();
    }
  }

  private mapRules(data: any): TenantBusinessRules {
    return {
      id: data.id,
      platformTenantId: data.platform_tenant_id,
      reminderRules: data.reminder_rules || this.getDefaultReminderRules(),
      slaRules: data.sla_rules || this.getDefaultSlaRules(),
      approvalRules: data.approval_rules || this.getDefaultApprovalRules(),
      flowRules: data.flow_rules || this.getDefaultFlowRules(),
      documentRules: data.document_rules || this.getDefaultDocumentRules(),
      onboardingRules: data.onboarding_rules || {},
      activityRules: data.activity_rules || {},
      notificationRules: data.notification_rules || {},
    };
  }

  private getDefaultRules(platformTenantId: string): TenantBusinessRules {
    return {
      id: '',
      platformTenantId,
      reminderRules: this.getDefaultReminderRules(),
      slaRules: this.getDefaultSlaRules(),
      approvalRules: this.getDefaultApprovalRules(),
      flowRules: this.getDefaultFlowRules(),
      documentRules: this.getDefaultDocumentRules(),
      onboardingRules: {},
      activityRules: {},
      notificationRules: {},
    };
  }

  private getDefaultReminderRules(): ReminderRules {
    return {
      initial_delay_hours: 72,
      followup_delay_hours: 48,
      max_reminders: 3,
      escalation_after_reminders: 3,
      role_specific: {},
    };
  }

  private getDefaultSlaRules(): SlaRules {
    return {
      onboarding_target_days: 14,
      credit_decision_hours: 24,
      document_review_hours: 48,
      escalation_thresholds: { warning: 0.75, critical: 1.0 },
    };
  }

  private getDefaultApprovalRules(): ApprovalRules {
    return {
      credit_auto_approve_limit: 5000,
      credit_require_director_above: 50000,
      document_approval_required: true,
      multi_level_approval: false,
      approval_chain: [],
    };
  }

  private getDefaultFlowRules(): FlowRules {
    return {
      require_all_roles: false,
      allow_role_skip: [],
      dependency_chain: {},
      auto_complete_on_all_roles: true,
    };
  }

  private getDefaultDocumentRules(): DocumentRules {
    return {
      required_by_client_type: {
        direct_client: ['vat_certificate', 'company_registration'],
        agent: ['agency_license'],
        supplier: ['bank_verification'],
      },
      expiry_warning_days: 30,
      auto_renewal_reminders: true,
    };
  }
}

export const BusinessRulesEngine = new BusinessRulesEngineClass();
export default BusinessRulesEngine;
